
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';

import { ApiService } from '../../../services/api.service';
import { ApiResponse, SmartPdfUploadResult } from '../../../models';

@Component({
  selector: 'app-smart-pdf-upload',
  standalone: true,
  imports: [],
  templateUrl: './smart-pdf-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './smart-pdf-upload.component.css'
})
export class SmartPdfUploadComponent {
  private readonly apiService = inject(ApiService);

  @Input() maxFileSizeBytes = 50 * 1024 * 1024;

  @Output() uploadStarted = new EventEmitter<string>();
  @Output() uploadSucceeded = new EventEmitter<SmartPdfUploadResult>();
  @Output() uploadFailed = new EventEmitter<string>();

  @ViewChild('smartPdfInput') smartPdfInput?: ElementRef<HTMLInputElement>;

  dragActive = false;
  uploading = false;
  selectedFileName = '';

  triggerBrowse(): void {
    if (this.uploading) {
      return;
    }

    this.smartPdfInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    this.validateAndUpload(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.uploading) {
      this.dragActive = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;

    if (this.uploading) {
      return;
    }

    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }

    this.validateAndUpload(file);
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private validateAndUpload(file: File): void {
    if (file.size <= 0) {
      this.uploadFailed.emit('Selected PDF file is empty.');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      this.uploadFailed.emit('Only .pdf files are supported.');
      return;
    }

    if (file.size > this.maxFileSizeBytes) {
      this.uploadFailed.emit(
        `File is too large (${this.formatBytes(file.size)}). Max allowed is ${this.formatBytes(this.maxFileSizeBytes)}.`
      );
      return;
    }

    this.selectedFileName = file.name;
    this.uploading = true;
    this.uploadStarted.emit(file.name);

    this.apiService.uploadSmartPdf(file).subscribe({
      next: (response: ApiResponse<SmartPdfUploadResult>) => {
        this.uploading = false;
        this.uploadSucceeded.emit(response.data);
      },
      error: (err: unknown) => {
        this.uploading = false;
        this.uploadFailed.emit(err instanceof Error ? err.message : 'Failed to upload PDF schematic.');
      }
    });
  }
}
